import React, { useMemo, useState } from 'react'
import { Container, Spinner, Tab } from 'react-bootstrap'

import { Tabs } from '../../components/Tabs/Tabs'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import { useGetBiDashboardsQuery } from '../../shared/insights/store/insights'
import { useGetRetentionPoliciesQuery } from '../../store/account/account'
import { RetentionResourceType, AuditLogRetention } from '../../models/RetentionPolicy'
import DashboardView from './DashboardView'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { AuditLogDisabledBox } from './AuditLogDisabledBox'
import { AuditLogSearchInfoBox } from './AuditLogSearchInfoBox'
import { type Dashboard } from '../../shared/insights/store/insights'
import { DELTA_TRIAL_FEATURES } from '../Plan/DeltaTrial/DeltaTrialFeatures'
import { Upsell } from '../Plan/DeltaTrial/Upsell/Upsell'

import './Analytics.scss'

const isAuditLogDashboard = (dashboard: Dashboard): boolean => dashboard.id === 'audit_log' || dashboard.name.toLowerCase().includes('audit log')

export const Analytics: React.FC = () => {
  useDocumentTitle('Analytics')

  const { hasQuota, isFetching: quotasFetching, quotaSuccess } = useZocksQuota()

  if (!quotaSuccess && quotasFetching) {
    return null
  }

  const insightsEnabled = hasQuota('insights.enabled.value')

  if (!insightsEnabled) {
    return (
      <AnalyticsWrapper>
        <div className='d-flex flex-grow-1 justify-content-center align-items-center'>
          <Upsell feature={DELTA_TRIAL_FEATURES['analytics']} />
        </div>
      </AnalyticsWrapper>
    )
  }

  return <AnalyticsContent />
}

const AnalyticsWrapper: React.FC<React.PropsWithChildren> = ({ children }) => {
  return <Container className='d-flex flex-column zocks-gap-1.25 p-0 w-100 mw-100 full-size-content'>{children}</Container>
}

const AnalyticsContent: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)

  const { data: dashboards, isFetching: isDashboardsFetching } = useGetBiDashboardsQuery({ accountId })
  const { data: retentionPolicies, isFetching: isRetentionPoliciesFetching } = useGetRetentionPoliciesQuery({ accountId })

  const [activeTabKey, setActiveTabKey] = useState<string | undefined>()

  const auditLogEnabled = useMemo(() => {
    if (isRetentionPoliciesFetching || !retentionPolicies) {
      return false
    }
    const searchableLogPolicy = retentionPolicies.data?.find(
      (policy) => policy.resource.type === RetentionResourceType.AUDIT_LOG && (policy.resource as AuditLogRetention).config?.kind === 'searchable_log',
    )
    if (!searchableLogPolicy) {
      return false
    }
    const defaultEnabled = (searchableLogPolicy.resource as AuditLogRetention).config?.defaultEnabled
    return defaultEnabled !== false
  }, [retentionPolicies, isRetentionPoliciesFetching])

  const changeTab = (key: string | null) => {
    if (key) {
      setActiveTabKey(key)
    }
  }

  return (
    <AnalyticsWrapper>
      {isDashboardsFetching || isRetentionPoliciesFetching ? (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      ) : null}
      {!isRetentionPoliciesFetching && dashboards && dashboards.length > 0 ? (
        <Tabs activeKey={activeTabKey} id='insightsTab' className='d-flex mb-3' onSelect={changeTab} mountOnEnter>
          {dashboards.map((dashboard) => {
            const showAuditLogDisabledBox = isAuditLogDashboard(dashboard) && !auditLogEnabled

            return (
              <Tab key={dashboard.id} eventKey={dashboard.id} title={dashboard.name}>
                <div>
                  {showAuditLogDisabledBox ? (
                    <AuditLogDisabledBox />
                  ) : (
                    <>
                      {isAuditLogDashboard(dashboard) && <AuditLogSearchInfoBox />}
                      <DashboardView dashboard={dashboard} initialHeight={1300} urlParams={{ accountId }}></DashboardView>
                    </>
                  )}
                </div>
              </Tab>
            )
          })}
        </Tabs>
      ) : null}
    </AnalyticsWrapper>
  )
}
